import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MonitorSmartphone, RefreshCw, ShieldOff } from 'lucide-react'
import { sessaoApi } from '@/services/api'
import type { SessaoUsuario } from '@/types/api'

function deviceLabel(userAgent: string) {
  if (/mobile|android|iphone/i.test(userAgent)) return 'Dispositivo móvel'
  if (/windows/i.test(userAgent)) return 'Windows'
  if (/macintosh|mac os/i.test(userAgent)) return 'macOS'
  if (/linux/i.test(userAgent)) return 'Linux'
  return 'Navegador'
}

export default function SessoesPage() {
  const [filter, setFilter] = useState<'all'|'active'|'inactive'>('active')
  const qc = useQueryClient()
  const { data = [], isLoading, refetch } = useQuery<SessaoUsuario[]>({
    queryKey: ['sessoes', filter],
    queryFn: () => sessaoApi.list(filter === 'all' ? undefined : filter === 'active').then(r => r.data)
  })
  const revoke = useMutation({
    mutationFn: (id: string) => sessaoApi.revoke(id, 'Revogada no painel administrativo'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessoes'] })
  })

  return <div className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-2xl font-bold text-white">Sessões</h1><p className="mt-1 text-slate-400">Monitore e encerre acessos ativos com segurança.</p></div>
      <button onClick={() => refetch()} className="flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-slate-200"><RefreshCw className="h-4 w-4"/>Atualizar</button>
    </div>
    <div className="flex gap-2">{([['active','Ativas'],['inactive','Encerradas'],['all','Todas']] as const).map(([value,label])=>
      <button key={value} onClick={()=>setFilter(value)} className={`rounded-lg px-4 py-2 text-sm ${filter===value?'bg-primary-600 text-white':'bg-slate-800 text-slate-300'}`}>{label}</button>)}</div>
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
      <table className="w-full"><thead><tr className="border-b border-slate-800 text-left text-sm text-slate-400">
        <th className="p-4">Usuário</th><th>Dispositivo</th><th>IP</th><th>Última atividade</th><th>Expiração</th><th>Status</th><th></th>
      </tr></thead><tbody>
        {isLoading ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">Carregando...</td></tr> :
        data.length===0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-500">Nenhuma sessão encontrada.</td></tr> :
        data.map(s=><tr key={s.id} className="border-b border-slate-800 text-sm text-slate-300">
          <td className="p-4 font-medium text-white">{s.nomeUsuario}</td>
          <td><span className="flex items-center gap-2"><MonitorSmartphone className="h-4 w-4 text-primary-400"/>{deviceLabel(s.userAgent)}</span></td>
          <td>{s.ipAddress}</td>
          <td>{new Date(s.dataUltimaAtividade || s.dataCriacao).toLocaleString()}</td>
          <td>{new Date(s.dataExpiracao).toLocaleString()}</td>
          <td><span className={`rounded-full px-2 py-1 text-xs ${s.isValida?'bg-emerald-500/10 text-emerald-400':'bg-slate-700 text-slate-400'}`}>{s.isValida?'Ativa':'Encerrada'}</span></td>
          <td>{s.isValida && <button onClick={()=>revoke.mutate(s.id)} disabled={revoke.isPending} title="Revogar sessão" className="rounded p-2 text-red-400 hover:bg-red-500/10"><ShieldOff className="h-4 w-4"/></button>}</td>
        </tr>)}
      </tbody></table>
    </div>
  </div>
}
